import React, { useState } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';
import { getCareSourceProfileSuggestions } from './careSourceStatusModel';

const ProfileSuggestionReview = ({
  source = {},
  onApplyProfileSuggestions,
  onError,
}) => {
  const [applying, setApplying] = useState(false);
  const [applySuccess, setApplySuccess] = useState(false);
  const suggestions = getCareSourceProfileSuggestions(source);
  const canApply = Boolean(
    suggestions.length
    && onApplyProfileSuggestions
    && source.reviewState !== 'reviewed'
    && source.profileSuggestionSummary?.needsReview !== false
  );

  if (!suggestions.length) {
    return null;
  }

  const handleApply = async () => {
    if (!canApply || applying) return;

    setApplying(true);
    onError?.('');
    setApplySuccess(false);
    try {
      await onApplyProfileSuggestions(source);
      setApplySuccess(true);
    } catch (error) {
      onError?.(error?.message || "We couldn't update the profile right now.");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Box
      sx={{
        borderRadius: 1.5,
        border: `1px solid ${alpha(colors.brand.tint, 0.25)}`,
        bgcolor: alpha(colors.brand.tint, 0.06),
        p: 1,
      }}
    >
      <Typography sx={{ fontSize: '0.8rem', fontWeight: 850, color: colors.semantic.ink }}>
        Profile suggestions
      </Typography>
      <Stack spacing={0.65} sx={{ mt: 0.75 }}>
        {suggestions.map((suggestion) => (
          <Box key={suggestion.key}>
            <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, color: alpha(colors.semantic.ink, 0.68) }}>
              {suggestion.label}
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: colors.semantic.ink, lineHeight: 1.45 }}>
              {suggestion.items.join(' • ')}
            </Typography>
          </Box>
        ))}
      </Stack>
      <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: 1 }}>
        {canApply ? (
          <Button
            size="small"
            variant="contained"
            onClick={handleApply}
            disabled={applying}
            sx={{
              borderRadius: '999px',
              px: 1.35,
              fontSize: '0.78rem',
              fontWeight: 850,
              textTransform: 'none',
              bgcolor: colors.brand.deep,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: colors.brand.ink,
                boxShadow: 'none',
              },
            }}
          >
            {applying ? 'Adding...' : 'Add to profile'}
          </Button>
        ) : null}
        {applySuccess || source.reviewState === 'reviewed' ? (
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: colors.semantic.success }}>
            Added to profile
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
};

export default ProfileSuggestionReview;
